// Notificaciones diarias de la app de celular (Android):
//   - 7:00 a. m.  "Momento de calma": una frase corta de mindfulness en espanol.
//   - 6:00 p. m.  "Mini noticia": un dato curioso en espanol + una palabra con
//                 su significado en ingles.
//
// Como funciona: al abrir la app se programan las notificaciones de los
// proximos 14 dias (cada dia con un texto distinto, tomado de los bancos de
// abajo). Cada vez que el estudiante abre la app se vuelven a programar, asi
// que siempre quedan dias por delante. No necesita internet ni servidor.
//
// En la pagina web (navegador) no hace nada: solo funciona en la app.
//
// Para agregar mas textos, basta con sumar lineas a CALMA o MINI_NOTICIAS
// (los textos nuevos entran en la rotacion automaticamente).

import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'

const CANAL = 'daily-spanish'
const DIAS_A_PROGRAMAR = 14
const HORA_CALMA = { h: 7, m: 0 }
const HORA_NOTICIA = { h: 18, m: 0 }
const CLAVE_APAGADO = 'eg_notificaciones_apagadas'
// Nuestros ids tienen 9 digitos (aaaammdd + 1 digito); asi no tocamos
// notificaciones de otras partes de la app, si las hubiera.
const ID_MINIMO_NUESTRO = 100000000

// Frases sencillas (nivel A2-B1) para que los extranjeros las entiendan.
const CALMA = [
  'Respira hondo tres veces. Inhala por la nariz y exhala por la boca. Lo estás haciendo muy bien.',
  'Relaja los hombros. Suelta la mandíbula. Respira despacio durante un minuto.',
  'Nombra cinco cosas que ves ahora. Fíjate en sus colores.',
  'Hoy estoy aprendiendo, y eso es suficiente. Sé amable contigo.',
  'Cierra los ojos durante 30 segundos. Escucha los sonidos a tu alrededor.',
  'Inhala contando hasta 4. Sostén 4. Exhala contando hasta 6.',
  'Los errores son prueba de que lo estás intentando. Sigue adelante.',
  'Pon una mano en el corazón y di: "Estoy tranquilo y estoy listo".',
  'Estira los brazos hacia arriba. Respira hondo. Suelta el aire.',
  'Piensa en una cosa por la que estás agradecido hoy.',
  'Toma un poco de agua despacio. Siente cómo es.',
  'No tienes que ser perfecto. Solo tienes que practicar.',
  'Siente tus pies en el suelo. Estás aquí. Estás bien.',
  'Deja el ayer atrás. Hoy es una página nueva.',
  'Sonríe suavemente. Una sonrisa pequeña puede cambiar tu ánimo.',
  'Camina despacio durante dos minutos. Siente cada paso.',
  'Una cosa a la vez. Ahora, solo respira.',
  'Dite algo amable en español hoy.',
  'Mira tus pensamientos como nubes en el cielo. Déjalos pasar.',
  'Descansa cinco minutos del celular. Mira por la ventana.',
  'El progreso es lento, pero es real. Siéntete orgulloso de tu esfuerzo.',
  'Inhala calma. Exhala estrés. Repite tres veces.',
  'Piensa en alguien que quieres y mándale un buen pensamiento.',
  'Tu mejor esfuerzo es suficiente por hoy.',
  'Escucha tu canción favorita. Cierra los ojos y disfrútala.',
  'Siente tu cuerpo. ¿Dónde hay tensión? Déjala ir poco a poco.',
  'Es normal sentir nervios al hablar un idioma nuevo. Ser valiente es hablar de todos modos.',
  'Cada paso pequeño cuenta. Diez minutos de español ya son un triunfo.',
  'Antes de dormir, recuerda una cosa buena de hoy.',
  'Pausa. Respira. Empieza otra vez.',
]

// Datos curiosos cortos y verdaderos, con una palabra de vocabulario.
const MINI_NOTICIAS = [
  'La letra ñ nació cuando los escribas juntaron dos n, una sobre la otra. Palabra: letra = letter.',
  'En español, "mañana" puede significar la parte temprana del día o el día siguiente. Palabra: mañana = morning / tomorrow.',
  'En español las preguntas empiezan con ¿ y las exclamaciones con ¡. Palabra: pregunta = question.',
  'Colombia tiene costas en el océano Pacífico y en el mar Caribe. Palabra: costa = coast.',
  'Colombia es uno de los mayores productores de café del mundo. Palabra: cosecha = harvest.',
  'Bogotá está a unos 2.600 metros sobre el nivel del mar. Palabra: altura = altitude.',
  'Colombia es el país con más especies de aves del mundo. Palabra: ave = bird.',
  'La Catedral de Sal de Zipaquirá está dentro de una mina, bajo la tierra. Palabra: sal = salt.',
  'Los Andes se dividen en Colombia en tres cordilleras. Palabra: montaña = mountain.',
  'La palma de cera del Quindío es el árbol nacional de Colombia y puede medir 60 metros. Palabra: árbol = tree.',
  'El vallenato, música de la costa colombiana, es patrimonio cultural inmaterial de la humanidad según la UNESCO. Palabra: música = music.',
  'Casi 500 millones de personas tienen el español como lengua materna. Palabra: lengua = language / tongue.',
  'El español es idioma oficial en 20 países. Palabra: país = country.',
  'Palabras como "tomate", "chocolate" y "aguacate" vienen del náhuatl, una lengua de México. Palabra: aguacate = avocado.',
  'El alfabeto español tiene 27 letras. Palabra: abecedario = alphabet.',
  'En español, la letra h es muda: "hola" se pronuncia "ola". Palabra: muda = silent.',
  'Los verbos "ser" y "estar" significan "to be", pero se usan en situaciones diferentes. Palabra: verbo = verb.',
  'Cartagena de Indias es Patrimonio de la Humanidad de la UNESCO desde 1984. Palabra: muralla = city wall.',
  'El Paisaje Cultural Cafetero de Colombia es Patrimonio de la Humanidad desde 2011. Palabra: finca = farm.',
  'Caño Cristales, en Colombia, es famoso como el "río de los cinco colores". Palabra: río = river.',
  'La orquídea es la flor nacional de Colombia. Palabra: flor = flower.',
  'El nombre "Colombia" viene de Cristóbal Colón. Palabra: nombre = name.',
  'La arepa es un alimento típico de Colombia y de Venezuela. Palabra: maíz = corn.',
  'La cumbia nació en la costa Caribe de Colombia. Palabra: baile = dance.',
  'Leer diez minutos al día en español ayuda a ampliar tu vocabulario. Palabra: mejorar = to improve.',
  'Repasar palabras nuevas antes de dormir puede ayudar a tu memoria. Palabra: memoria = memory.',
  'Tu corazón late unas 100.000 veces al día. Palabra: latir = to beat.',
  'Las mariposas pueden saborear con las patas. Palabra: mariposa = butterfly.',
  'Los delfines duermen con la mitad del cerebro despierta. Palabra: dormir = to sleep.',
  'Una caminata de 20 minutos al día es buena para el cuerpo y el ánimo. Palabra: caminata = walk.',
]

// Numero de dia (fecha local) para rotar los textos: el mismo dia siempre
// da el mismo texto, y cada dia cambia al siguiente.
function numeroDeDia(fecha) {
  return Math.floor(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()) / 86400000)
}

function idDeNotificacion(fecha, ranura) {
  const mm = String(fecha.getMonth() + 1).padStart(2, '0')
  const dd = String(fecha.getDate()).padStart(2, '0')
  return parseInt(`${fecha.getFullYear()}${mm}${dd}${ranura}`, 10)
}

function armarLista(ahora) {
  const lista = []
  for (let i = 0; i < DIAS_A_PROGRAMAR; i++) {
    const dia = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + i)
    const n = numeroDeDia(dia)

    const horaCalma = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), HORA_CALMA.h, HORA_CALMA.m, 0)
    if (horaCalma > ahora) {
      const texto = CALMA[n % CALMA.length]
      lista.push({
        id: idDeNotificacion(dia, 1),
        title: 'Momento de calma 🌿',
        body: texto,
        largeBody: texto,
        channelId: CANAL,
        schedule: { at: horaCalma, allowWhileIdle: true },
        isExactNotification: false,
      })
    }

    const horaNoticia = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), HORA_NOTICIA.h, HORA_NOTICIA.m, 0)
    if (horaNoticia > ahora) {
      const texto = MINI_NOTICIAS[(n + 7) % MINI_NOTICIAS.length]
      lista.push({
        id: idDeNotificacion(dia, 2),
        title: 'Mini noticia 📰',
        body: texto,
        largeBody: texto,
        channelId: CANAL,
        schedule: { at: horaNoticia, allowWhileIdle: true },
        isExactNotification: false,
      })
    }
  }
  return lista
}

async function cancelarNuestras() {
  const { notifications } = await LocalNotifications.getPending()
  const nuestras = notifications.filter(n => n.id >= ID_MINIMO_NUESTRO)
  if (nuestras.length > 0) {
    await LocalNotifications.cancel({ notifications: nuestras.map(n => ({ id: n.id })) })
  }
}

// Llamar cuando el estudiante entra a la app (ya con sesion iniciada).
export async function programarNotificacionesDiarias() {
  if (!Capacitor.isNativePlatform()) return
  try {
    if (localStorage.getItem(CLAVE_APAGADO) === '1') return

    let permiso = await LocalNotifications.checkPermissions()
    if (permiso.display === 'prompt' || permiso.display === 'prompt-with-rationale') {
      permiso = await LocalNotifications.requestPermissions()
    }
    if (permiso.display !== 'granted') return

    await LocalNotifications.createChannel({
      id: CANAL,
      name: 'Español diario',
      description: 'Momentos de calma y mini noticias en español',
      importance: 3,
      visibility: 1,
    })

    await cancelarNuestras()
    const lista = armarLista(new Date())
    if (lista.length > 0) await LocalNotifications.schedule({ notifications: lista })
  } catch (err) {
    // Las notificaciones nunca deben romper la app
    console.warn('No se pudieron programar las notificaciones:', err)
  }
}

// Para un futuro boton de ajustes ("Recibir recordatorios diarios")
export async function desactivarNotificacionesDiarias() {
  try {
    localStorage.setItem(CLAVE_APAGADO, '1')
    if (Capacitor.isNativePlatform()) await cancelarNuestras()
  } catch (err) { console.warn(err) }
}

export async function activarNotificacionesDiarias() {
  try { localStorage.removeItem(CLAVE_APAGADO) } catch (err) { /* sin storage */ }
  await programarNotificacionesDiarias()
}

// Solo para pruebas
export const _bancos = { CALMA, MINI_NOTICIAS, armarLista }
